import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationsProvider } from './contexts/NotificationsContext';
import NotificationBell from './components/NotificationBell';
import Homepage from './components/Homepage';
import Login from './components/Login';
import Register from './components/Register';
import Dashboard from './components/Dashboard';
import Friends from './components/Friends';
import Invitations from './components/Invitations';
import History from './components/History';
import Profile from './components/Profile';
import ZbioWallet from './components/ZbioWallet';
import Chat from './components/Chat';
import GameRoomRouter from './components/GameRoomRouter';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import InstallPrompt from './components/InstallPrompt';
import Notification from './components/Notification';
import LanguageSelector from './components/LanguageSelector';
import './i18n'; // Initialize i18n
import './App.css';
import './styles/dashboard.css';
import './components/common/Drawer.css';

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

// Public Route component (redirects to dashboard if already authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <Navigate to="/dashboard" /> : <>{children}</>;
};

// Root route: marketing homepage on tellcrypt.com, login/dashboard on game subdomain
const HomeRoute: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();
  const isGameSubdomain =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'game.tellcrypt.com' ||
      window.location.hostname.startsWith('game.'));

  if (loading) {
    return <div>Loading...</div>;
  }

  if (isGameSubdomain) {
    return <Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />;
  }

  return <Homepage />;
};

// Layout wrapper to conditionally show Sidebar or Navbar
const PAGE_TITLE_KEYS: Record<string, string> = {
  '/dashboard': 'navigation.dashboard',
  '/wallet': 'navigation.wallet',
  '/friends': 'navigation.friends',
  '/chat': 'navigation.chat',
  '/invitations': 'navigation.invitations',
  '/history': 'navigation.history',
  '/profile': 'navigation.profile',
};

const LayoutWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close the mobile drawer whenever the route changes
  React.useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Don't show sidebar/navbar on homepage, login/register pages, or game rooms
  const isHomepage = location.pathname === '/';
  const isAuthPage = location.pathname === '/login' || location.pathname === '/register';
  const isGameRoom = location.pathname.includes('/game/') || location.pathname.startsWith('/games/') || location.pathname.startsWith('/room/');
  
  if (isHomepage || isAuthPage || isGameRoom) {
    return <>{children}</>;
  }

  if (!isAuthenticated) {
    return (
      <>
        <Navbar />
        <div className="page-with-sidebar">{children}</div>
      </>
    );
  }

  const titleKey = PAGE_TITLE_KEYS[location.pathname];
  const pageTitle = titleKey ? t(titleKey) : 'Tellcrypt';

  // Show sidebar for authenticated users (with mobile burger top bar)
  return (
    <>
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="app-topbar">
        <div className="app-topbar-left">
          <button
            className="ui-burger"
            aria-label="Open menu"
            onClick={() => setSidebarOpen(true)}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
          <span className="mobile-topbar-title">{pageTitle}</span>
        </div>
        <NotificationBell />
      </div>
      <div className="page-with-sidebar">
        {children}
      </div>
    </>
  );
};

const AppContent: React.FC = () => {
  const [notifications, setNotifications] = useState<Array<{
    id: string;
    message: string;
    type: 'success' | 'error' | 'info';
  }>>([]);

  const addNotification = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    const id = Date.now().toString();
    setNotifications(prev => [...prev, { id, message, type }]);
  };

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  return (
    <Router>
      <NotificationsProvider>
      <div className="App">
        <LayoutWrapper>
          <Routes>
            <Route path="/login" element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } />
            <Route path="/register" element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/friends" element={
              <ProtectedRoute>
                <Friends />
              </ProtectedRoute>
            } />
            <Route path="/invitations" element={
              <ProtectedRoute>
                <Invitations />
              </ProtectedRoute>
            } />
            <Route path="/chat" element={
              <ProtectedRoute>
                <Chat />
              </ProtectedRoute>
            } />
            <Route path="/room/:gameId" element={
              <ProtectedRoute>
                <GameRoomRouter />
              </ProtectedRoute>
            } />
            <Route path="/games/:gameId" element={
              <ProtectedRoute>
                <GameRoomRouter />
              </ProtectedRoute>
            } />
            <Route path="/history" element={
              <ProtectedRoute>
                <History />
              </ProtectedRoute>
            } />

            <Route path="/profile" element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            } />
            <Route path="/wallet" element={
              <ProtectedRoute>
                <ZbioWallet />
              </ProtectedRoute>
            } />
            <Route path="/" element={
              <HomeRoute />
            } />
          </Routes>
        </LayoutWrapper>
        
        {/* PWA Install Prompt */}
        <InstallPrompt />
        
        {/* Notifications */}
        {notifications.map(notification => (
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>
      </NotificationsProvider>
    </Router>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App; 